import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./EventsList.module.css";

const EVENT_TAB_STORAGE_KEY = "dashboardEventsMobileTab";
const EVENTS_CACHE_KEY = "eventsData";
const EVENTS_CACHE_TIME_KEY = "eventsDataTimestamp";
const EVENTS_CACHE_TTL = 5 * 60 * 1000;

const readCachedEventsSync = () => {
  try {
    const rawData = localStorage.getItem(EVENTS_CACHE_KEY);
    const rawTimestamp = localStorage.getItem(EVENTS_CACHE_TIME_KEY);

    if (!rawData || !rawTimestamp) return null;

    const timestamp = Number(rawTimestamp);
    const isFresh = Date.now() - timestamp < EVENTS_CACHE_TTL;

    if (!isFresh) return null;

    const parsed = JSON.parse(rawData);

    return {
      past: parsed?.past || [],
      current: parsed?.current || [],
      future: parsed?.future || [],
    };
  } catch {
    return null;
  }
};

function EventsList() {
  const navigate = useNavigate();

  const initialCachedEvents = readCachedEventsSync();

  const [events, setEvents] = useState(
    initialCachedEvents || { past: [], current: [], future: [] }
  );
  const [loading, setLoading] = useState(!initialCachedEvents);
  const [loadingPast, setLoadingPast] = useState(false);
  const [pastLoaded, setPastLoaded] = useState(
    (initialCachedEvents?.past || []).length > 0
  );
  const [error, setError] = useState(null);

  const [expandedSections, setExpandedSections] = useState({
    past: false,
    current: true,
    future: true,
  });

  const [activeMobileTab, setActiveMobileTab] = useState(() => {
    return localStorage.getItem(EVENT_TAB_STORAGE_KEY) || "current";
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlyDebt] = useState(false);

  const buildEventsDataForStorage = useCallback((current, future, past) => {
    return {
      current,
      future,
      past,
    };
  }, []);

  const getValidCachedEvents = useCallback(() => {
    try {
      const rawData = localStorage.getItem(EVENTS_CACHE_KEY);
      const rawTimestamp = localStorage.getItem(EVENTS_CACHE_TIME_KEY);

      if (!rawData || !rawTimestamp) return null;

      const timestamp = Number(rawTimestamp);
      const isFresh = Date.now() - timestamp < EVENTS_CACHE_TTL;

      if (!isFresh) return null;

      const parsed = JSON.parse(rawData);

      return {
        past: parsed?.past || [],
        current: parsed?.current || [],
        future: parsed?.future || [],
      };
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(EVENT_TAB_STORAGE_KEY, activeMobileTab);
  }, [activeMobileTab]);

  const normalizeActiveResponse = useCallback((data) => {
    return {
      past: [],
      current:
        data?.current ||
        data?.grouped?.current ||
        data?.events?.filter((e) => e.relevance === "current") ||
        [],
      future:
        data?.future ||
        data?.grouped?.future ||
        data?.events?.filter((e) => e.relevance === "future") ||
        [],
    };
  }, []);

  const normalizePastResponse = useCallback((data) => {
    return {
      past:
        data?.past ||
        data?.grouped?.past ||
        data?.events?.filter((e) => e.relevance === "past") ||
        [],
    };
  }, []);

  const fetchPastEvents = useCallback(async () => {
    const response = await fetch("/api/events/past-list", {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });

    if (response.status === 401) {
      localStorage.removeItem(EVENTS_CACHE_KEY);
      localStorage.removeItem(EVENTS_CACHE_TIME_KEY);
      navigate("/login");
      throw new Error("Сессия недействительна. Войдите снова.");
    }

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(data?.message || "Ошибка загрузки прошлых мероприятий");
    }

    return normalizePastResponse(data).past || [];
  }, [navigate, normalizePastResponse]);

  useEffect(() => {
    const controller = new AbortController();
    let mounted = true;

    const fetchActiveEvents = async () => {
      try {
        setLoading(true);
        setError(null);

        const cachedEvents = getValidCachedEvents();

        if (cachedEvents) {
          setEvents(cachedEvents);
          setPastLoaded((cachedEvents.past || []).length > 0);
          setLoading(false);

          if (!cachedEvents.past || cachedEvents.past.length === 0) {
            setLoadingPast(true);

            fetchPastEvents()
              .then((nextPast) => {
                if (!mounted) return;

                setEvents((prev) => {
                  const nextEvents = {
                    ...prev,
                    past: nextPast,
                  };

                  localStorage.setItem(
                    EVENTS_CACHE_KEY,
                    JSON.stringify(
                      buildEventsDataForStorage(
                        nextEvents.current,
                        nextEvents.future,
                        nextEvents.past
                      )
                    )
                  );
                  localStorage.setItem(
                    EVENTS_CACHE_TIME_KEY,
                    String(Date.now())
                  );

                  return nextEvents;
                });

                setPastLoaded(true);
              })
              .catch((err) => {
                if (!mounted) return;
                console.error("Ошибка фоновой загрузки прошлых мероприятий:", err);
              })
              .finally(() => {
                if (mounted) setLoadingPast(false);
              });
          }

          return;
        }

        const response = await fetch("/api/events/active-list", {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });

        if (!mounted) return;

        if (response.status === 401) {
          setError("Сессия недействительна. Войдите снова.");
          localStorage.removeItem(EVENTS_CACHE_KEY);
          localStorage.removeItem(EVENTS_CACHE_TIME_KEY);
          navigate("/login");
          return;
        }

        const data = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(data?.message || "Ошибка загрузки мероприятий");
        }

        const normalized = normalizeActiveResponse(data);

        const nextEvents = {
          past: [],
          current: normalized.current || [],
          future: normalized.future || [],
        };

        setEvents(nextEvents);

        localStorage.setItem(
          EVENTS_CACHE_KEY,
          JSON.stringify(
            buildEventsDataForStorage(
              nextEvents.current,
              nextEvents.future,
              nextEvents.past
            )
          )
        );
        localStorage.setItem(EVENTS_CACHE_TIME_KEY, String(Date.now()));

        setLoading(false);
        setLoadingPast(true);

        fetchPastEvents()
          .then((nextPast) => {
            if (!mounted) return;

            setEvents((prev) => {
              const nextWithPast = {
                ...prev,
                past: nextPast,
              };

              localStorage.setItem(
                EVENTS_CACHE_KEY,
                JSON.stringify(
                  buildEventsDataForStorage(
                    nextWithPast.current,
                    nextWithPast.future,
                    nextWithPast.past
                  )
                )
              );
              localStorage.setItem(EVENTS_CACHE_TIME_KEY, String(Date.now()));

              return nextWithPast;
            });

            setPastLoaded(true);
          })
          .catch((err) => {
            if (!mounted) return;
            console.error("Ошибка фоновой загрузки прошлых мероприятий:", err);
          })
          .finally(() => {
            if (mounted) setLoadingPast(false);
          });
      } catch (err) {
        if (!mounted) return;
        if (err?.name === "AbortError") return;

        setError(err?.message || "Ошибка при загрузке мероприятий");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchActiveEvents();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [
    navigate,
    buildEventsDataForStorage,
    getValidCachedEvents,
    fetchPastEvents,
    normalizeActiveResponse,
  ]);

  const loadPastEvents = useCallback(async () => {
    if (loadingPast || pastLoaded) return;

    try {
      setLoadingPast(true);
      setError(null);

      const nextPast = await fetchPastEvents();

      setEvents((prev) => {
        const nextEvents = {
          ...prev,
          past: nextPast,
        };

        localStorage.setItem(
          EVENTS_CACHE_KEY,
          JSON.stringify(
            buildEventsDataForStorage(
              nextEvents.current,
              nextEvents.future,
              nextEvents.past
            )
          )
        );
        localStorage.setItem(EVENTS_CACHE_TIME_KEY, String(Date.now()));

        return nextEvents;
      });

      setPastLoaded(true);
    } catch (err) {
      setError(err?.message || "Ошибка при загрузке прошлых мероприятий");
    } finally {
      setLoadingPast(false);
    }
  }, [loadingPast, pastLoaded, fetchPastEvents, buildEventsDataForStorage]);

  const toggleSection = async (section) => {
    const nextExpanded = !expandedSections[section];

    setExpandedSections((prev) => ({ ...prev, [section]: nextExpanded }));

    if (section === "past" && nextExpanded && !pastLoaded) {
      await loadPastEvents();
    }
  };

  const handleMobileTabChange = async (tab) => {
    setActiveMobileTab(tab);

    if (tab === "past" && !pastLoaded) {
      await loadPastEvents();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("ru-RU");
  };

  const sortByDate = (list, direction = "asc") => {
    return [...list].sort((a, b) => {
      const dateA = new Date(a.dateBegin || 0).getTime();
      const dateB = new Date(b.dateBegin || 0).getTime();
      return direction === "asc" ? dateA - dateB : dateB - dateA;
    });
  };

  const matchesFilters = (event) => {
    const q = searchTerm.trim().toLowerCase();

    const matchesSearch =
      !q ||
      event.name?.toLowerCase().includes(q) ||
      event.id?.toLowerCase().includes(q) ||
      event.managerName?.toLowerCase().includes(q) ||
      event.location?.toLowerCase().includes(q);

    const matchesDebt =
      !showOnlyDebt || (event.debt && parseFloat(event.debt) > 0);

    return matchesSearch && matchesDebt;
  };

  const filteredEvents = {
    past: sortByDate((events.past || []).filter(matchesFilters), "desc"),
    current: sortByDate((events.current || []).filter(matchesFilters), "asc"),
    future: sortByDate((events.future || []).filter(matchesFilters), "asc"),
  };

  const renderEventCard = (event, index) => (
    <div
      key={`${event.id}-${event.relevance || "event"}-${event.dateBegin || "no-date"}-${index}`}
      className={styles.eventCard}
      onClick={() => navigate(`/events/${event.id}`)}
      style={{ cursor: "pointer" }}
    >
      <h5 className={styles.eventName}>{event.name} | {event.id}</h5>

      {event.debt !== undefined && event.debt !== null && event.debt > 0 && (
        <div className={styles.debtBadge}>
          Задолженность:{" "}
          <strong>
            {parseFloat(event.debt).toLocaleString("ru-RU", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </strong>
        </div>
      )}

      <p>
        <strong>Даты:</strong> {formatDate(event.dateBegin)} - {formatDate(event.dateEnd)}
      </p>
      <p>
        <strong>Место:</strong> {event.location}
      </p>
    </div>
  );

  const renderEventsContent = (list, emptyText, isPastTab = false) => {
    if (isPastTab && loadingPast && !pastLoaded) {
      return (
        <p className={styles.loadingInline}>
          Загрузка прошлых мероприятий...
        </p>
      );
    }

    if (list.length > 0) {
      return list.map(renderEventCard);
    }

    return <p className={styles.noEvents}>{emptyText}</p>;
  };

  if (loading) {
    return <div className={styles.loading}>Загрузка мероприятий...</div>;
  }

  if (error) {
    return <div className={styles.error}>Ошибка: {error}</div>;
  }

  return (
    <div className={styles.eventsContainer}>

      <div className={styles.searchPanel}>
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Поиск.."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className={styles.clearSearch}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {(searchTerm || showOnlyDebt) && (
        <div className={styles.searchStats}>
          <strong>Найдено:</strong>
          <span className={styles.statsNumbers}>
            <span className={styles.pastStat}>
              {filteredEvents.past.length} прошлых
            </span>
            <span className={styles.currentStat}>
              {filteredEvents.current.length} текущих
            </span>
            <span className={styles.futureStat}>
              {filteredEvents.future.length} будущих
            </span>
          </span>
        </div>
      )}

      <div className={styles.mobileTabsBlock}>
        <div className={styles.mobileTabs}>
          <button
            type="button"
            className={`${styles.mobileTab} ${
              activeMobileTab === "current" ? styles.mobileTabActive : ""
            }`}
            onClick={() => handleMobileTabChange("current")}
          >
            Текущие
          </button>

          <button
            type="button"
            className={`${styles.mobileTab} ${
              activeMobileTab === "future" ? styles.mobileTabActive : ""
            }`}
            onClick={() => handleMobileTabChange("future")}
          >
            Будущие
          </button>

          <button
            type="button"
            className={`${styles.mobileTab} ${
              activeMobileTab === "past" ? styles.mobileTabActive : ""
            }`}
            onClick={() => handleMobileTabChange("past")}
          >
            Прошлые
          </button>
        </div>

        <div className={styles.mobilePanel}>
          {activeMobileTab === "current" && (
            <div className={styles.mobileEventsList}>
              {renderEventsContent(
                filteredEvents.current,
                "Нет текущих мероприятий"
              )}
            </div>
          )}

          {activeMobileTab === "future" && (
            <div className={styles.mobileEventsList}>
              {renderEventsContent(
                filteredEvents.future,
                "Нет будущих мероприятий"
              )}
            </div>
          )}

          {activeMobileTab === "past" && (
            <div className={styles.mobileEventsList}>
              {renderEventsContent(
                filteredEvents.past,
                "Нет прошлых мероприятий",
                true
              )}
            </div>
          )}
        </div>
      </div>

      <div className={styles.desktopLayout}>
        <div className={styles.currentFutureContainer}>
          <div className={styles.currentSection}>
            <div className={styles.sectionHeader}>
              <h4>Текущие мероприятия ({filteredEvents.current.length})</h4>
            </div>

            <div className={styles.eventsList}>
              {renderEventsContent(
                filteredEvents.current,
                "Нет текущих мероприятий"
              )}
            </div>
          </div>

          <div className={styles.futureSection}>
            <div className={styles.sectionHeader}>
              <h4>Будущие мероприятия ({filteredEvents.future.length})</h4>
            </div>

            <div className={styles.eventsList}>
              {renderEventsContent(
                filteredEvents.future,
                showOnlyDebt
                  ? "Нет будущих мероприятий с задолженностью"
                  : "Нет будущих мероприятий"
              )}
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <div
            className={styles.sectionHeaderPast}
            onClick={() => toggleSection("past")}
          >
            <h4>Прошлые мероприятия</h4>
            <span className={styles.arrow}>
              {expandedSections.past ? "▲" : "▼"}
            </span>
          </div>

          {expandedSections.past && (
            <div className={styles.eventsList}>
              {loadingPast && !pastLoaded ? (
                <p className={styles.loadingInline}>
                  Загрузка прошлых мероприятий...
                </p>
              ) : (
                renderEventsContent(
                  filteredEvents.past,
                  showOnlyDebt
                    ? "Нет прошлых мероприятий с задолженностью"
                    : "Нет прошлых мероприятий"
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EventsList;